/**
 * The view of a specialist's page, as the address bar holds it: which of
 * their visits are listed, and which page of them. A default is not written
 * into the address.
 */
import { dayOf, momentsOfDays } from "@/shared/lib/days";

import type { OnlineSpan, RatingsShown } from "../api/queries";
import { VISIT_STATUSES, type VisitStatus } from "../api/types";

export const RATINGS_SHOWN = ["hidden", "shown"] as const satisfies readonly RatingsShown[];

export interface PersonSearch {
  /** A visit's status: only their visits in it. */
  visits?: VisitStatus;
  /** The page of their visits. */
  vpage?: number;
  /** Which of their ratings: only the hidden, or only the shown. */
  ratings?: RatingsShown;
  /** The first day of the stretches at work, `YYYY-MM-DD` by the market's clock. */
  ofrom?: string;
  /** The last day of them, the whole of it. */
  oto?: string;
}

/** Reads the address. Anything it does not know is dropped, never trusted. */
export function parsePersonSearch(raw: Readonly<Record<string, unknown>>): PersonSearch {
  const search: PersonSearch = {};
  const status = VISIT_STATUSES.find((known) => known === raw["visits"]);
  if (status !== undefined) search.visits = status;
  const page = Number(raw["vpage"]);
  if (Number.isInteger(page) && page > 1) search.vpage = page;
  const ratings = RATINGS_SHOWN.find((known) => known === raw["ratings"]);
  if (ratings !== undefined) search.ratings = ratings;
  const from = dayOf(raw["ofrom"]);
  if (from !== undefined) search.ofrom = from;
  const to = dayOf(raw["oto"]);
  if (to !== undefined) search.oto = to;
  return search;
}

/**
 * The span the stretches at work are asked for: the days chosen as moments
 * of the market's clock. With no day chosen nothing is sent, and hg-order
 * answers with its last week.
 */
export function onlineSpan(search: PersonSearch): OnlineSpan {
  return momentsOfDays(search.ofrom, search.oto);
}

/** The view after another status of visits was chosen: their first page, the rest of the view kept. */
export function withVisitStatus(search: PersonSearch, status: string | undefined): PersonSearch {
  const { visits: _, vpage: __, ...rest } = search;
  const known = VISIT_STATUSES.find((each) => each === status);
  return known === undefined ? rest : { ...rest, visits: known };
}

export function withVisitPage(search: PersonSearch, page: number): PersonSearch {
  const { vpage: _, ...rest } = search;
  return page > 1 ? { ...rest, vpage: page } : rest;
}
